import React, { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { ConflictError, NotFoundError } from "../../api";
import {
  extractAssets,
  extractInstrumentationTypeTree,
  extractPictures,
  extractPrimaryValue,
} from "../../extractors";
import { accessRightsShape, assetShape } from "../../shapes";
import { convertSpecsToAttributes } from "../../utils/specificationUtils";
import { addUnitToValue } from "../../utils/valueUtils";
import {
  ActionBar,
  ActionBarButtons,
  DeleteActionButton,
  EditActionButton,
} from "../ActionBar";
import NavigationBreadCrumbs from "../Breadcrumb/NavigationBreadCrumbs";
import { Details, DetailsItem } from "../Details";
import { ToggleLabel } from "../Form";
import { Column, Container, Row } from "../Grid";
import Loader from "../Loader";
import More from "../More";
import AssetsAssignList, {
  AssetAssignObject,
} from "../ObjectAssignList/AssetsAssignList";
import NodesAssignList from "../ObjectAssignList/NodesAssignList";
import SpecificationDetails from "../Specifications/SpecificationDetails";
import InstrumentationDetailsBasicInfo from "./InstrumentationDetailsBasicInfo";
import InstrumentationFullEmptyCalibrationHelp from "./InstrumentationFullEmptyCalibrationHelp";

import {
  apiShape,
  useBrowser,
  useNotifier,
  useRules,
  withApi,
} from "../../context";
import { useApi } from "../../hooks";
import {
  accessibilityIconClassName,
  criticalityIconClassName,
  extractCode,
  nameForCriticality,
  nameForSelections,
  sortBy,
} from "../../utils";
import { enforceAccessRightCanRead, withAccessRights } from "../../wrappers";

type Props = {
  api: apiShape;
  children?: React.ReactNode;
  match?: {
    params?: {
      id?: string;
    };
  };
  shouldCollapse?: boolean;
  appSpecificMore?: React.ReactNode;
  appShowsFullEmptyCalibration?: boolean;
  appShowsMediumType?: boolean;
  accessRights: accessRightsShape;
};

type ExtendedOrderCodeResponse = {
  extended_order_code?: string;
};

export function InstrumentationDetails(props: Props) {
  const {
    api,
    match,
    shouldCollapse,
    appSpecificMore,
    appShowsFullEmptyCalibration,
    appShowsMediumType,
    children,
    accessRights,
  } = props;

  const intl = useIntl();
  const rules = useRules();
  const browser = useBrowser();
  const notifier = useNotifier();
  const apiHook = useApi();

  const { id } = match!.params!;
  const [assets, setAssets] = useState<assetShape[]>();
  const [instrumentation, setInstrumentation] = useState<
    AssetAssignObject | undefined
  >();
  const [firmwareVersion, setFirmwareVersion] = useState();
  const [sensorBuildNr, setSensorBuildNr] = useState();
  const [fetching, setFetching] = useState(true);

  const stringToBool = (string: $TSFixMe) => string === "true";

  const primaryAsset = assets?.[0];

  const orderCodeLookUp =
    primaryAsset?.manufacturerName === "Endress+Hauser" &&
    primaryAsset?.specifications?.["eh.pcps.tmp.ordercode"]?.value &&
    /^[a-zA-Z0-9]+-+[a-zA-Z0-9]+\/[a-zA-Z0-9]+/g.exec(
      primaryAsset.specifications["eh.pcps.tmp.ordercode"].value,
    );

  const orderCode =
    primaryAsset?.specifications?.["eh.pcps.tmp.ordercode"]?.value;

  const shouldFetchEoc = !!orderCodeLookUp && !!orderCode;

  // Extended order code is required to derive if a product is FWR30 or FWR30v10
  const { data: extendedOrderCodeResponse } =
    apiHook.get.useQuery<ExtendedOrderCodeResponse>(
      "/endress/extended_order_code_lookup",
      { order_code: orderCode },
      {
        enabled: shouldFetchEoc,
        retry: false,
        meta: { useGlobalErrorHandling: false },
      },
    );

  useEffect(() => {
    if (extendedOrderCodeResponse?.extended_order_code) {
      setAssets((prevAssets: assetShape[] | undefined) => {
        const extendedOrderCode = extractCode(
          extendedOrderCodeResponse?.extended_order_code,
        );
        /* istanbul ignore next */
        if (!prevAssets) return prevAssets;
        const updatedAssets = [...prevAssets];
        updatedAssets[0] = {
          ...updatedAssets[0],
          extendedOrderCode,
        };

        return updatedAssets;
      });
    }
  }, [extendedOrderCodeResponse]);

  const currentRules = rules.instrumentation(instrumentation, assets);
  const showHealthNotifications =
    rules?.application().get("showHealthNotifications") &&
    accessRights?.canUpdate;
  const prepareInstrumentationForDisplay = (apiResponse: $TSFixMe) => {
    let newInstrumentation = apiResponse;
    const specsWhitelist = [
      "eh.iba.environmental_condition",
      "eh.iba.special_demands",
      "eh.iba.criticality_quality",
      "eh.iba.criticality_environment",
      "eh.iba.criticality_safety",
      // The following specs are shared by value & inventory.
      // They get displayed by appShowsMediumType & appShowsFullEmptyCalibration (bools in props)
      "eh.user_config.medium_type",
      "eh.user_config.unit",
      "eh.user_config.empty",
      "eh.user_config.full",
      "eh.user_config.sensitivity",
      "eh.user_config.block_distance",
    ];
    newInstrumentation.itemType = "instrumentation";
    newInstrumentation.type = extractInstrumentationTypeTree(apiResponse.type);
    newInstrumentation.pictures = extractPictures(apiResponse);
    // @ts-expect-error TS(2554) FIXME: Expected 2 arguments, but got 1.
    newInstrumentation.value = extractPrimaryValue(apiResponse.values);
    newInstrumentation = convertSpecsToAttributes(
      newInstrumentation,
      apiResponse.specifications,
      specsWhitelist,
    );
    newInstrumentation.worst_asset_status = apiResponse.worst_asset_status;
    return newInstrumentation;
  };

  const loadData = async () => {
    try {
      const apiResponse = await api.get(
        `/instrumentations/${id}`,
        {
          include:
            "type,status,pictures,specifications,type.parent,type.tenant,worst_asset_status,paths",
        },
        false,
      );
      const responseAssets = await api.get(`/instrumentations/${id}/assets`, {
        include:
          "specifications,product.manufacturer,product.tenant,product.pictures,status,product.specifications,additional_data_history_period",
      });
      const loadedAssets = sortBy(
        extractAssets(responseAssets),
        "serialNumber",
      );
      if (loadedAssets.length) {
        setSensorBuildNr(loadedAssets[0].sensorModuleBuildNumber);
        const responseSoftwares = await api.get(
          `/assets/${loadedAssets[0].id}/softwares`,
          { software_type_id: 1 },
          false,
        );
        if (responseSoftwares.softwares?.length) {
          setFirmwareVersion(responseSoftwares.softwares.version_number);
        }
      }
      const instrumentationForDisplay =
        prepareInstrumentationForDisplay(apiResponse);
      const instrumentationRules = rules.instrumentation(
        instrumentationForDisplay,
        loadedAssets,
      );
      instrumentationForDisplay.unit = instrumentationRules
        ?.get("fullEmptyCalibrationUnitOptions")
        ?.filter(
          (unit: $TSFixMe) => unit.id === instrumentationForDisplay.unit,
        )[0];

      setInstrumentation(instrumentationForDisplay);
      setAssets(loadedAssets);
      setFetching(false);
    } catch (error) {
      if (error instanceof NotFoundError) {
        browser.navigateTo("/404");
      } else {
        notifier.showError(api.translateError(error));
        setFetching(false);
      }
    }
  };

  React.useEffect(() => {
    loadData();
  }, [id]);

  const onConfirmDelete = async () => {
    try {
      await api.delete(`/instrumentations/${id}`);
      notifier.showSuccess(
        intl.formatMessage({
          id: "instrumentation.actions.delete.notification",
        }),
      );
      browser.goBack();
    } catch (error) {
      if (error instanceof ConflictError) {
        notifier.showError(
          intl.formatMessage({
            id: "api.error.instrumentation.assigned_restriction",
          }),
        );
      } else {
        notifier.showError(intl.formatMessage({ id: "api.error.unknown" }));
      }
    }
  };

  const renderConfigurations = () => {
    const sensitivityValue = () =>
      // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
      instrumentation.sensitivity
        ? intl.formatMessage({
            // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
            id: `enum.sensitivity.${instrumentation.sensitivity}`,
          })
        : intl.formatMessage({ id: "enum.sensitivity.medium" });
    return (
      <>
        <h2 id="full_empty_calibration">
          <FormattedMessage id="label.full_empty_calibration" />
          <InstrumentationFullEmptyCalibrationHelp />
        </h2>
        <DetailsItem
          id="instrumentation-empty"
          translationKey="label.empty"
          value={addUnitToValue(
            // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
            instrumentation.empty,
            // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
            instrumentation.unit,
            "-",
          )}
        />
        <DetailsItem
          id="instrumentation-full"
          translationKey="label.full"
          value={addUnitToValue(
            // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
            instrumentation.full,
            // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
            instrumentation.unit,
            "-",
          )}
        />
        <DetailsItem
          id="instrumentation-block-distance"
          translationKey="label.block_distance"
          value={addUnitToValue(
            // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
            instrumentation.block_distance,
            // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
            instrumentation.unit,
            intl.formatMessage({ id: "label.automatic" }),
          )}
        />
        <DetailsItem
          id="instrumentation-sensitivity"
          translationKey="label.sensitivity"
          value={sensitivityValue()}
        />
      </>
    );
  };

  const renderMediumType = () => {
    // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
    const returnDetail = instrumentation.medium_type ? (
      <DetailsItem
        // @ts-expect-error TS(2322) FIXME: Type '{ intl: intlShape; id: string; translationKe... Remove this comment to see the full error message
        intl={intl}
        id="instrumentation-medium-type"
        translationKey="label.medium_type"
        value={intl.formatMessage({
          // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
          id: `enum.medium_type.${instrumentation.medium_type}`,
        })}
      />
    ) : null;
    return returnDetail;
  };

  const renderMoreContent = () => {
    const detailItems = instrumentation ? (
      <>
        <DetailsItem
          id="instrumentation-description"
          translationKey="label.description"
          value={(instrumentation as $TSFixMe).description}
        />
        {appShowsMediumType &&
        currentRules.get("needsMediumType") &&
        currentRules?.get(
          "isCapableOfMeasuringSolids",
          firmwareVersion,
          sensorBuildNr,
        )
          ? renderMediumType()
          : null}
        {appShowsFullEmptyCalibration &&
        currentRules.get("needsFullEmptySettings")
          ? renderConfigurations()
          : null}
        <DetailsItem
          id="instrumentation-accessibility"
          icon={accessibilityIconClassName(
            (instrumentation as $TSFixMe).accessibility,
          )}
          translationKey="label.accessibility"
          value={nameForSelections(
            "enum.accessibility",
            (instrumentation as $TSFixMe).accessibility,
            intl,
          )}
        />
        <DetailsItem
          id="instrumentation-criticality"
          icon={criticalityIconClassName(
            (instrumentation as $TSFixMe).criticality,
          )}
          translationKey="label.criticality"
          value={nameForCriticality(
            (instrumentation as $TSFixMe).criticality,
            intl,
          )}
        />
        <div className="space-after" />
        <DetailsItem
          id="instrumentation-labels"
          translationKey="label.tag_labels"
        >
          <ToggleLabel
            id="criticality-environment"
            text={intl.formatMessage({ id: "label.criticality_env" })}
            initial={stringToBool(
              (instrumentation as $TSFixMe).criticality_environment,
            )}
          />
          <ToggleLabel
            id="criticality-quality"
            text={intl.formatMessage({ id: "label.criticality_quality" })}
            initial={stringToBool(
              (instrumentation as $TSFixMe).criticality_quality,
            )}
          />
          <ToggleLabel
            id="criticality-safety"
            text={intl.formatMessage({ id: "label.criticality_safety" })}
            initial={stringToBool(
              (instrumentation as $TSFixMe).criticality_safety,
            )}
          />
          <ToggleLabel
            id="special-demands"
            // @ts-expect-error TS(2769) FIXME: No overload matches this call.
            disabled
            text={intl.formatMessage({ id: "label.special_demands" })}
            initial={stringToBool(
              (instrumentation as $TSFixMe).special_demands,
            )}
          />
          <ToggleLabel
            id="environmental-condition"
            text={intl.formatMessage({ id: "label.environmental_condition" })}
            initial={
              (instrumentation as $TSFixMe).environmental_condition === "heavy"
            }
          />
        </DetailsItem>
        {appSpecificMore}
      </>
    ) : null;

    return (
      <Row>
        <Column>
          {shouldCollapse && !fetching ? (
            <div className="space-before">
              <More>{detailItems}</More>
            </div>
          ) : (
            detailItems
          )}
        </Column>
      </Row>
    );
  };

  const handleChildComponent = (child: $TSFixMe) =>
    child
      ? React.cloneElement(child, {
          parentInstrumentation: instrumentation,
          parentAssets: assets,
        })
      : /* istanbul ignore next */ child;

  const renderWorstAssetStatus = () =>
    // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
    instrumentation.worst_asset_status ? (
      <DetailsItem
        id="instrumentation-worst-asset-status"
        translationKey="label.status"
        // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
        icon={`icon lcm-iot-icon-circle status-${instrumentation.worst_asset_status.code} ${instrumentation.status.code === "inactive" ? "inactive" : ""} `}
        headerSuffix={
          showHealthNotifications ? (
            <button
              id="edit-notification-link"
              className="btn-link header-side-button icon2-eh-mail"
              type="button"
              onClick={() =>
                browser.navigateTo(
                  `/instrumentations/${id}/edit_health_notifications`,
                )
              }
            />
          ) : null
        }
        // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
        value={instrumentation.worst_asset_status.name}
      />
    ) : null;

  const renderInstrumentationDetails = () =>
    instrumentation && assets ? (
      <>
        <InstrumentationDetailsBasicInfo
          pictures={(instrumentation as $TSFixMe).pictures}
          tag={(instrumentation as $TSFixMe).tag}
          typeDisplayName={(instrumentation as $TSFixMe).type.displayName}
        />
        <DetailsItem
          id="instrumentation-status"
          translationKey="label.operation_mode"
          value={(instrumentation as $TSFixMe).status.name}
        />
        {renderWorstAssetStatus()}
        <SpecificationDetails
          specifications={(instrumentation as $TSFixMe).specifications}
        />
      </>
    ) : null;

  const appSpecificChildren =
    instrumentation && assets && children
      ? React.Children.map(children, (child) => handleChildComponent(child))
      : null;

  return (
    <Container>
      <NavigationBreadCrumbs paths={(instrumentation as $TSFixMe)?.paths} />
      <Row>
        <Column>
          <ActionBar>
            <h1>
              <FormattedMessage id="instrumentation_details.header" />
            </h1>
            <ActionBarButtons>
              <EditActionButton
                id="edit-instrumentation-button"
                disabled={!accessRights?.canUpdate}
                target={`/instrumentations/${match!.params!.id}/edit`}
              />
              <DeleteActionButton
                id="delete-instrumentation"
                disabled={!accessRights?.canDelete || fetching}
                modalTitle={intl.formatMessage({
                  id: "instrumentation.actions.delete.modal_title",
                })}
                modalMessage={intl.formatMessage({
                  id: "instrumentation.actions.delete.modal_message",
                })}
                onConfirm={onConfirmDelete}
              />
            </ActionBarButtons>
          </ActionBar>
          <Details>
            {renderInstrumentationDetails()}
            {renderMoreContent()}
          </Details>
          {appSpecificChildren}
          <AssetsAssignList
            object={instrumentation}
            accessRights={accessRights}
            onAssignedAssetsChanged={loadData}
            assets={assets ?? []}
          />
          <NodesAssignList
            // @ts-expect-error TS(2322) FIXME: Type '{ object: undefined; accessRights: accessRig... Remove this comment to see the full error message
            object={instrumentation}
            accessRights={accessRights}
          />
          <Loader id="instrumentation-details-loader" loading={fetching} />
        </Column>
      </Row>
    </Container>
  );
}

export default withApi(
  withAccessRights(
    InstrumentationDetails,
    "Instrumentation",
    enforceAccessRightCanRead,
  ),
);
